import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { BaseActionHandler, tokenForProperty } from "./base";
import { DescriptionToken } from "./action-handler";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { makeText } from "../builder-utils";
import { makePhoneNumberPropertyDescriptor } from "./phone-call";
import { asMaybeString, asString } from "../../computation-model/data";
import { makeSMSURL } from "../../urls";

interface TextMessageActionDescription extends ActionDescription {
    readonly number?: PropertyDescription;
    readonly body?: PropertyDescription;
}

export class TextMessageWithArgsHandler extends BaseActionHandler<TextMessageActionDescription> {
    public readonly kind = ActionKind.TextMessageWithArgs;

    public readonly iconName = "message-circle";

    public getDescriptor(
        _desc: TextMessageActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Compose SMS",
            needsScreenContext: true,
            properties: [
                makePhoneNumberPropertyDescriptor(mutatingScreenKind),
                makeTextPropertyDescriptor("body", "Message", "Enter message body", false, mutatingScreenKind, {
                    preferredNames: ["message", "sms", "body", "text"],
                    preferredType: "string",
                    searchable: false,
                    emptyByDefault: true,
                    columnFirst: true,
                }),
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: TextMessageActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Compose SMS action");
        }

        const number = makeText(builder, desc.number, tables.input, true, false, false, false, screenContext);
        const body = makeText(builder, desc.body, tables.input, true, true, false, false, screenContext);
        if (number === undefined) return undefined;

        return b.actionIfContextDefined(b.textMessage(number, body));
    }

    public getTokenizedDescription(
        desc: TextMessageActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.number, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [
            {
                kind: "string",
                value: "to ",
            },
            columnToken,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: TextMessageActionDescription): WireActionHydrator | undefined {
        const [numberGetter] = ib.getValueGetterForProperty(desc.number, false);
        const [bodyGetter] = ib.getValueGetterForProperty(desc.body, true);

        return vp => {
            const number = asString(numberGetter(vp));
            const messageBody = asMaybeString(bodyGetter(vp));

            const url = makeSMSURL(number, messageBody);
            if (url === undefined) return undefined;

            return async ab => {
                ab.actionCallbacks.openLink(url);
                return true;
            };
        };
    }
}
